import type { ReactElement } from 'react';

import type { ColumnDef, Row } from '@tanstack/react-table';
import { ColumnHeaderMenu } from 'shared/ui/data-table/ColumnHeaderMenu';
import { numberFn, selectEqualsFn, textIncludesFn } from 'shared/ui/data-table/filterFns';
import { formatPropertyPrice } from 'shared/utils/formatPropertyPrice';

import { Badge } from '@/components/ui/badge';

import { isPropertyAvailable, type Property } from '../../api/properties';

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const renderText = (value: string | number | null | undefined, className = 'text-sm'): ReactElement => (
  <span className={className}>{value !== null && value !== undefined && value !== '' ? value : '—'}</span>
);

const availableFn = (row: Row<Property>, _id: string, value: boolean): boolean =>
  value === isPropertyAvailable(row.original);

interface BuildPropertyColumnsArgs {
  types: string[];
  orientations: string[];
}

export const buildPropertyColumns = ({ types, orientations }: BuildPropertyColumnsArgs): ColumnDef<Property>[] => [
  {
    accessorKey: 'id',
    meta: { title: 'ID' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="ID" sortable={false} />,
    cell: ({ row }) => <span className="font-mono text-xs text-text-subtle">{row.original.id}</span>,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'unit',
    meta: { title: 'Unidad' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Unidad" filter={{ type: 'text' }} />,
    cell: ({ row }) => <span className="font-semibold text-brand-navy">{row.original.unit ?? '—'}</span>,
    filterFn: textIncludesFn,
  },
  {
    accessorKey: 'model',
    meta: { title: 'Modelo' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Modelo" filter={{ type: 'text' }} />,
    cell: ({ row }) => renderText(row.original.model),
    filterFn: textIncludesFn,
  },
  {
    accessorKey: 'projectReferenceSKU',
    meta: { title: 'SKU' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="SKU" filter={{ type: 'text' }} />,
    cell: ({ row }) => renderText(row.original.projectReferenceSKU, 'font-mono text-xs text-text-subtle'),
    filterFn: textIncludesFn,
  },
  {
    accessorKey: 'totalPrice',
    meta: { title: 'Precio total' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Precio total" filter={{ type: 'number' }} />,
    cell: ({ row }) => (
      <span className="font-semibold text-brand-vivid">
        {formatPropertyPrice(row.original.totalPrice, row.original.priceCurrency)}
      </span>
    ),
    filterFn: numberFn,
  },
  {
    accessorKey: 'price',
    meta: { title: 'Precio' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Precio" filter={{ type: 'number' }} />,
    cell: ({ row }) => (
      <span className="text-sm">{formatPropertyPrice(row.original.price, row.original.priceCurrency)}</span>
    ),
    filterFn: numberFn,
  },
  {
    accessorKey: 'discountPercentage',
    meta: { title: 'Descuento' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Descuento" filter={{ type: 'number' }} />,
    cell: ({ row }) => {
      const d = row.original.discountPercentage;
      return d !== null && d !== undefined ? (
        <Badge variant="outline">{`${d}%`}</Badge>
      ) : (
        <span className="text-sm text-text-subtle">—</span>
      );
    },
    filterFn: numberFn,
  },
  {
    accessorKey: 'bedrooms',
    meta: { title: 'Dormitorios' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Dorm." filter={{ type: 'number' }} />,
    cell: ({ row }) => <Badge variant="outline">{row.original.bedrooms ?? 0}</Badge>,
    filterFn: numberFn,
  },
  {
    accessorKey: 'bathrooms',
    meta: { title: 'Baños' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Baños" filter={{ type: 'number' }} />,
    cell: ({ row }) => <Badge variant="outline">{row.original.bathrooms ?? 0}</Badge>,
    filterFn: numberFn,
  },
  {
    accessorKey: 'interiorSize',
    meta: { title: 'm² Int.' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="m² Int." filter={{ type: 'number' }} />,
    cell: ({ row }) => renderText(row.original.interiorSize ? `${row.original.interiorSize} m²` : null),
    filterFn: numberFn,
  },
  {
    accessorKey: 'terraceSize',
    meta: { title: 'm² Terraza' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="m² Terraza" filter={{ type: 'number' }} />,
    cell: ({ row }) => renderText(row.original.terraceSize ? `${row.original.terraceSize} m²` : null),
    filterFn: numberFn,
  },
  {
    accessorKey: 'orientation',
    meta: { title: 'Orientación' },
    header: ({ column }) => (
      <ColumnHeaderMenu column={column} title="Orientación" filter={{ type: 'select', options: orientations }} />
    ),
    cell: ({ row }) => renderText(row.original.orientation),
    filterFn: selectEqualsFn,
  },
  {
    // Assumes numeric floors; non-numeric values (e.g. "PB", "S1") silently fail the numeric filter.
    accessorKey: 'floor',
    meta: { title: 'Piso' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Piso" filter={{ type: 'number' }} />,
    cell: ({ row }) => renderText(row.original.floor),
    filterFn: numberFn,
  },
  {
    accessorKey: 'description',
    meta: { title: 'Descripción' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Descripción" sortable={false} />,
    cell: ({ row }) => (
      <span className="block max-w-xs truncate text-sm text-text-subtle" title={row.original.description ?? ''}>
        {row.original.description ?? '—'}
      </span>
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'propertyType',
    meta: { title: 'Tipo' },
    header: ({ column }) => (
      <ColumnHeaderMenu column={column} title="Tipo" filter={{ type: 'select', options: types }} />
    ),
    cell: ({ row }) => renderText(row.original.propertyType, 'text-sm text-text-subtle'),
    filterFn: selectEqualsFn,
  },
  {
    accessorKey: 'available',
    meta: { title: 'Disponible' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Disponible" filter={{ type: 'boolean' }} />,
    cell: ({ row }): ReactElement => {
      const isAvailable = isPropertyAvailable(row.original);
      return <Badge variant={isAvailable ? 'default' : 'destructive'}>{isAvailable ? 'Sí' : 'No'}</Badge>;
    },
    sortingFn: (a, b) => Number(isPropertyAvailable(a.original)) - Number(isPropertyAvailable(b.original)),
    filterFn: availableFn,
  },
  {
    accessorKey: 'rentPrice',
    meta: { title: 'Arriendo' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Arriendo" sortable={false} />,
    cell: ({ row }) => (
      <span className="text-sm">
        {formatPropertyPrice(row.original.rentPrice, row.original.rentPriceUnit ?? row.original.priceCurrency)}
      </span>
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'year',
    meta: { title: 'Año' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Año" filter={{ type: 'number' }} />,
    cell: ({ row }) => renderText(row.original.year),
    filterFn: numberFn,
  },
  {
    accessorKey: 'createdAt',
    meta: { title: 'Creado' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Creado" />,
    cell: ({ row }) => <span className="text-xs text-text-subtle">{formatDate(row.original.createdAt)}</span>,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'updatedAt',
    meta: { title: 'Actualizado' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Actualizado" />,
    cell: ({ row }) => <span className="text-xs text-text-subtle">{formatDate(row.original.updatedAt)}</span>,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'projectReferenceId',
    meta: { title: 'Referencia' },
    header: ({ column }) => <ColumnHeaderMenu column={column} title="Referencia" sortable={false} />,
    cell: ({ row }) => renderText(row.original.projectReferenceId, 'font-mono text-xs text-text-subtle'),
    enableSorting: false,
    enableColumnFilter: false,
  },
];
